"use client";

import * as React from "react";
import { StaticImageData } from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { CATALOG_ENTRIES_DEFAULTS, CE_10 } from "@stadium/shared/src/helpers/plasmic/static-slices";

import styles from "./main.module.scss";
import Link from "next/link";

gsap.registerPlugin(ScrollTrigger);

const EXPANDED_HEIGHT = "17.6875rem";

export interface CardItem {
  id: string;
  title: string;
  description: string;
  buttonText: string;
  buttonUrl: string;
  image: string | StaticImageData;
  imageAlt?: string;
}

export interface CE10Props {
  contentHeading?: string;
  contentDescription?: string;
  cards?: CardItem[];
  backgroundColor?: string;
  className?: string;
}

const CE10_DEFAULTS = CATALOG_ENTRIES_DEFAULTS[CE_10];

function expandCard(card: HTMLElement, descHeights: Map<HTMLElement, number>) {
  gsap.to(card, {
    height: EXPANDED_HEIGHT,
    duration: 1.2,
    ease: "power3.inOut",
  });

  card.classList.add(styles.mobileExpanded);

  const desc = card.querySelector<HTMLElement>(`.${styles.featuredDescription}`);
  if (desc) {
    const targetHeight = descHeights.get(desc) || desc.scrollHeight;
    gsap.to(desc, {
      height: targetHeight,
      opacity: 1,
      duration: 0.8,
      delay: 0.2,
      ease: "power3.inOut",
    });
  }
}

function cleanupMobileCards(allCards: HTMLElement[], rightCards: HTMLElement[]) {
  allCards.forEach((c) => c.classList.remove(styles.mobileExpanded));
  rightCards.forEach((card) => {
    const overlay = card.querySelector<HTMLElement>(`.${styles.rightButtonOverlay}`);
    const desc = card.querySelector<HTMLElement>(`.${styles.featuredDescription}`);
    if (overlay) gsap.set(overlay, { clearProps: "paddingTop" });
    if (desc) gsap.set(desc, { clearProps: "all" });
  });
}

function setupMobileScrollAnimation(featuredCard: HTMLElement, rightCards: HTMLElement[]) {
  const allCards = [featuredCard, ...rightCards];

  const descHeights: Map<HTMLElement, number> = new Map();
  rightCards.forEach((card) => {
    const desc = card.querySelector<HTMLElement>(`.${styles.featuredDescription}`);
    if (desc) {
      descHeights.set(desc, desc.scrollHeight);
      gsap.set(desc, { height: 0, opacity: 0 });
    }
  });

  featuredCard.classList.add(styles.mobileExpanded);

  allCards.forEach((card, index) => {
    if (index >= allCards.length - 1) return;
    const nextCard = allCards[index + 1];
    ScrollTrigger.create({
      trigger: card,
      start: "top top",
      onEnter: () => expandCard(nextCard, descHeights),
    });
  });

  const refreshTimeout = setTimeout(() => ScrollTrigger.refresh(), 100);

  return () => {
    clearTimeout(refreshTimeout);
    cleanupMobileCards(allCards, rightCards);
  };
}

const CE10: React.FC<Readonly<CE10Props>> = ({
  contentHeading = CE10_DEFAULTS?.contentHeading,
  contentDescription = CE10_DEFAULTS?.contentDescription,
  cards = CE10_DEFAULTS?.cards,
  backgroundColor = CE10_DEFAULTS?.backgroundColor,
  className = "",
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const featuredCardRef = React.useRef<HTMLDivElement>(null);
  const rightCardRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  React.useEffect(() => {
    if (!containerRef.current || !featuredCardRef.current) return;

    const mm = gsap.matchMedia();
    const featured = featuredCardRef.current;
    const rightCards = rightCardRefs.current.filter(Boolean) as HTMLElement[];

    mm.add("(max-width: 767px)", () => setupMobileScrollAnimation(featured, rightCards));

    return () => mm.revert();
  }, []);

  if (!cards.length) return null;

  return (
    <section className={`${styles.sectionWrapper} ${className}`} ref={containerRef}>
      <div className={styles.container}>
        <div className={styles.desktopGrid}>
          {/* Left Content Column */}
          <div className={styles.leftColumn}>
            <div className={styles.contentBox} style={{ backgroundColor }}>
              <h2 className={styles.contentHeading}>{contentHeading}</h2>
              <p className={styles.contentDescription}>{contentDescription}</p>
            </div>

            {/* First Card - Featured */}
            <div className={styles.featuredCard} ref={featuredCardRef}>
              <Multimedia
                type="image"
                src={cards[0].image}
                alt={cards[0].imageAlt || cards[0].title}
                className={styles.featuredCardImage}
              />
              <div className={styles.featuredOverlay}>
                <Link href={cards[0].buttonUrl} className={styles.linkGroup}>
                  <span className={styles.linkPill}>{cards[0].buttonText}</span>
                  <span className={styles.linkCircle}></span>
                </Link>
                <p className={styles.featuredDescription}>{cards[0].description}</p>
              </div>
            </div>
          </div>

          {/* Right Column - Two Cards */}
          <div className={styles.rightColumn}>
            {cards.slice(1).map((card, index) => (
              <div
                key={card.id}
                className={styles.rightCard}
                ref={(el) => {
                  rightCardRefs.current[index] = el;
                }}
              >
                <Multimedia
                  type="image"
                  src={card.image}
                  alt={card.imageAlt || card.title}
                  className={styles.rightCardImage}
                />
                <div className={styles.rightButtonOverlay}>
                  <Link href={card.buttonUrl} className={styles.linkGroup}>
                    <span className={styles.linkPill}>{card.buttonText}</span>
                    <span className={styles.linkCircle}></span>
                  </Link>
                  <p className={styles.featuredDescription}>{card.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CE10;
